import { Component, EventEmitter, Inject, LOCALE_ID, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Business, IBusinessListResponse } from '@carm/_core/access-control-mgt/models/user-role.model';
import { AccessUserService } from '@carm/_core/access-control-mgt/services';
import { BasePageComponent } from '@carm/_utils/base-page.component';
import { IKeyValue } from '@carm/_utils/models';
import { takeUntil } from 'rxjs/operators';
import {
  AgGridConfig,
  ACTIONTYPES,
  IHandleServerDataSource,
  IHandlePagination,
} from '../table/table-ag-grid/models/table-actions-model';
import { IFilterSearchCriteria } from '../table-filter/models/table-filter.model';
import { UserService } from '@carm/_core/access-control-mgt/facade/user.service';

@Component({
  selector: 'carm-business-selector-modal',
  templateUrl: './business-selector-modal.component.html',
  styleUrls: ['./business-selector-modal.component.scss'],
})
export class BusinessSelectorModalComponent
  extends BasePageComponent
  implements OnInit {
  @Output() companyUidSelected = new EventEmitter<Business>();
  @Output() selectionCancelled = new EventEmitter();
  public businessList: IHandleServerDataSource;
  public nativeBusiness$: Observable<Business>;
  public isCurrentNativeBusinessSelected$: Observable<boolean>;
  public nativeUid = '';
  public displayCurrentNativeBusiness = true;

  public filterOptions = [
    {
      key: 'legalName',
      value: this.translateService.instant(
        'business-selector-modal.filter_options.name'
      ),
    },
    {
      key: 'uid',
      value: this.translateService.instant(
        'business-selector-modal.filter_options.bn9'
      ),
    },
  ];
  public serverSortingMapper: IKeyValue[] = [
    { key: 'legalName', value: 'legalName' },
    { key: 'uid', value: 'uid' },
  ];


  agGridConfig: AgGridConfig = {
    rowModelType: 'infinite',
    hideQuickFilter: true,
    checkbox: false,
    columnDefs: [
      {
        headerName: this.translateService.instant(
          'business_selector.businessName'
        ),
        field: 'legalName',
        sortable: true,
        filter: false,
        cellRenderer: (params) => {
          const building = 'fa-building';
          const home = 'fa-onboarding-documentation';
          let icon = '';

          if(this.nativeUid !== undefined && params.data !== undefined){
            if(this.nativeUid === params.data.uid){
              icon = home;
            } else{
              icon = building;
            }

            return `<span class="${icon} fas"></span>` + params.value;
          }
        }
      },
      {
        headerName: this.translateService.instant(
          'business_selector.businessNumber'
        ),
        field: 'uid',
        sortable: true,
        filter: false,
      },
    ],
    actions: [
      {
        actionType: ACTIONTYPES.select,
        isActionEnabled: (rowData: Business) => {
          return (
            !this.data?.currentBusinessId ||
            rowData?.uid !== this.data?.currentBusinessId
          );
        },
      },
    ],
  };

  constructor(
    public dialogRef: MatDialogRef<BusinessSelectorModalComponent>,
    protected translateService: TranslateService,
    private accessUserService: AccessUserService,
    protected userService: UserService,
    @Inject(LOCALE_ID) protected localeId: string,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      title: string;
      currentBusinessId: string;
      filterToBusinessManagement?: boolean;
      loadBusinessCallback?: (event?: IHandlePagination) => Observable<IBusinessListResponse>;
      displayNativeBusiness?: boolean;
    }
  ) {
    super();
  }

  ngOnInit(): void {
    this.userService.getUserBusiness().pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.nativeUid =  res.uid;
    });
    this.loadBusinessList();
    this.displayNativeBusinessName();
  }

  private displayNativeBusinessName(): void {
    this.nativeBusiness$ = this.userService.getUserBusiness();
    this.isCurrentNativeBusinessSelected$ = this.userService.isSelectedBusinessNative();
    if (this.data?.displayNativeBusiness !== undefined && this.data?.displayNativeBusiness !== null) {
      this.displayCurrentNativeBusiness = this.data.displayNativeBusiness;
    }
  }

  public loadBusinessList(event: IFilterSearchCriteria = null): void {
    const filterField = event ? event.criteriaField : undefined;
    const filterValue = event ? event.criteriaValue : undefined;
    this.businessList = {
      defaultSortColumn: 'legalName',
      sortByColumns: this.serverSortingMapper,
      handleError: e => this.trySetPageErrorMessages(e),
      getRows: (
        pageIndex: number,
        pageSize: number,
        sort: string
      ): Observable<IBusinessListResponse> => {
        const pagingData = {
          pageIndex,
          pageSize,
          sort,
          filterField,
          filterValue,
        } as IHandlePagination;
        if (!!this.data?.loadBusinessCallback) {
          return this.data?.loadBusinessCallback(pagingData);
        }
        return this.getListOfBusinesses(pagingData);
      },
    };
  }

  manageButtonActions(event: { buttonId: ACTIONTYPES; data: Business }) {
    if (
      event.buttonId === ACTIONTYPES.select &&
      (!this.data?.currentBusinessId ||
        event.data?.uid !== this.data?.currentBusinessId)
    ) {
      this.companyUidSelected.emit(event.data);
      this.closeDialog();
    }
  }

  setNativeAsCurrentBusiness(nativeBusiness: Business){
    this.companyUidSelected.emit(nativeBusiness);
    this.closeDialog();
  }

  closeDialog(mode?: string): void {
    if (mode === 'cancelled') {
      this.selectionCancelled.emit();
    }
    this.dialogRef.close();
  }

  private getListOfBusinesses(data: IHandlePagination): Observable<IBusinessListResponse> {
    return this.accessUserService
      .getUserBusinessList(
        data.pageIndex,
        data.pageSize,
        data.filterField,
        data.filterValue,
        data.sort,
        this.data.filterToBusinessManagement
      )
      .pipe(takeUntil(this.destroy$));
  }
}
